import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";

const buildInitialState = (initialData) => ({
  id: initialData?.id || uuidv4(),
  logisticCompany: initialData?.logisticCompany || "",
  branch: initialData?.branch || "",
  description: initialData?.description || "",
  isDefault: initialData?.isDefault ?? true,
});

const ForwardAddressDialog = ({ open, onClose, onSave, initialData }) => {
  const isEdit = Boolean(initialData);
  const [address, setAddress] = useState(() => buildInitialState(initialData));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setAddress(buildInitialState(initialData));
      setErrors({});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setAddress((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!address.logisticCompany) e.logisticCompany = "Required";
    if (!address.branch.trim()) e.branch = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(address);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      sx={{ "& .MuiDialog-paper": { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {isEdit ? "Edit Forward Address" : "Add a New Forward Address"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.logisticCompany}>
              <InputLabel>Logistic Company *</InputLabel>
              <Select
                label="Logistic Company *"
                name="logisticCompany"
                value={address.logisticCompany}
                onChange={handleChange}
              >
                <MenuItem value="HAL Express">HAL Express</MenuItem>
                <MenuItem value="Mixay Express">Mixay Express</MenuItem>
                <MenuItem value="Anousith Express">Anousith Express</MenuItem>
              </Select>
              {errors.logisticCompany && <FormHelperText>{errors.logisticCompany}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Branch *"
              name="branch"
              value={address.branch}
              onChange={handleChange}
              error={!!errors.branch}
              helperText={errors.branch}
              placeholder="e.g. Vientiane Main Branch"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes / Description"
              name="description"
              value={address.description}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder="Account number, recipient name, or any special instructions..."
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={address.isDefault}
                  onChange={handleChange}
                  name="isDefault"
                  color="secondary"
                />
              }
              label="Set as default forward address"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="secondary" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="secondary" variant="contained" sx={{ borderRadius: 2, minWidth: 120 }}>
          {isEdit ? "Save Changes" : "Add Address"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardAddressDialog;
